import styled from "styled-components";

export const PageWrap = styled.div`
  flex: 1;
  background: var(--mp-bodyBackground, #f7f8fc);
  color: var(--mp-textColor, #171717);
  font-family: 'Liberation Serif', Georgia, serif;
  overflow-x: hidden;
  transition: background-color 0.3s ease, color 0.3s ease;
`;

/* ─── Hero Section ─── */
export const HeroSection = styled.section`
  max-width: 1680px;
  margin: 0 auto;
  padding: 72px 40px 56px;
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 48px;
  align-items: center;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    padding: 48px 20px 40px;
    gap: 32px;
  }

  @media (max-width: 480px) {
    padding: 32px 16px 28px;
  }
`;

export const HeroLeft = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const HeroBadge = styled.div`
  margin-bottom: 20px;
  font-family: Liberation Serif;
  font-weight: 700;
  font-style: bold;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 2.8px;
  vertical-align: middle;
  text-transform: uppercase;
  color: var(--mp-featureIconColor, #D863BA);

  [data-marketplace-theme="dark"] &,
  .dark & {
    background-image: var(--mp-heroAccent, linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  @media (max-width: 480px) {
    font-size: 11px;
    letter-spacing: 0.8px;
  }
`;

export const HeroTitle = styled.h1`
  margin: 0 0 20px 0;
  font-family: Liberation Serif;
  font-weight: 700;
  font-style: bold;
  font-size: 48px;
  line-height: 52.8px;
  letter-spacing: -0.96px;
  vertical-align: middle;
  color: var(--mp-headingColor, #171717);
  max-width: 700px;

  span.gradient {
    font-style: italic;
    display: inline-block;
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  [data-marketplace-theme="light"] & span.gradient,
  :root:not([data-marketplace-theme="dark"]) & span.gradient {
    background: var(--mp-heroAccent, linear-gradient(95.57deg, #FFD573 0%, #D863BA 100%));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 36px;
    line-height: 44px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    line-height: 36px;
  }
`;

export const HeroDesc = styled.p`
  margin: 0 0 32px 0;
  font-family: Liberation Serif;
  font-weight: 400;
  font-style: regular;
  font-size: 18px;
  line-height: 28px;
  letter-spacing: 0px;
  vertical-align: middle;
  color: var(--mp-heroSubColor, #434655);
  max-width: 540px;
  opacity: 0.9;

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 22px;
    margin-bottom: 24px;
  }
`;

export const HeroButtons = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

export const PrimaryBtn = styled.button`
  padding: 16px 32px;
  border-radius: 9999px;
  font-family: 'Liberation Serif', serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  background: var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%));
  color: #171717;
  transition: transform 0.2s, opacity 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    opacity: 0.95;
    box-shadow: 0 8px 24px rgba(216, 99, 186, 0.25);
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    color: #402D00;
    box-shadow: 0 8px 24px rgba(255, 201, 81, 0.2);

    &:hover {
      box-shadow: 0 8px 24px rgba(201, 77, 170, 0.35);
    }
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 14px 24px;
    font-size: 14px;
  }
`;

export const SecondaryBtn = styled.button`
  padding: 16px 32px;
  border-radius: 9999px;
  font-family: 'Liberation Serif', serif;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.85);
  color: var(--mp-headingColor, #171717);
  border: 1.5px solid rgba(201, 77, 170, 0.6);
  box-shadow: 0 4px 16px rgba(201, 77, 170, 0.08);
  transition: background-color 0.2s, transform 0.2s, border-color 0.2s, box-shadow 0.2s;

  &:hover {
    background: #ffffff;
    transform: translateY(-2px);
    border-color: #C94DAA;
    box-shadow: 0 8px 24px rgba(201, 77, 170, 0.18);
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255, 255, 255, 0.15);
    color: var(--mp-headingColor, #ffffff);

    &:hover {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.35);
    }
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 14px 24px;
    font-size: 14px;
  }
`;

export const HeroRight = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const HeroImageCard = styled.div`
  position: relative;
  width: 100%;
  max-width: 520px;
  aspect-ratio: 1.05 / 1;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(23, 23, 23, 0.08);

  [data-marketplace-theme="dark"] &,
  .dark & {
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
    border-color: rgba(255, 255, 255, 0.06);
  }
`;

export const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const GlassOverlay = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  right: 20px;
  padding: 18px 20px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: rgba(21, 24, 27, 0.82);
    border: 1px solid rgba(255, 255, 255, 0.08);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  }
`;

export const OverlayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 6px;
`;

export const ActiveBadge = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #10b981;
  background: rgba(16, 185, 129, 0.12);
  padding: 4px 10px;
  border-radius: 100px;
  white-space: nowrap;
`;

export const OverlayTitle = styled.h3`
  margin: 0;
  font-family: 'Liberation Serif', serif;
  font-size: 18px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
`;

export const OverlaySubtitle = styled.p`
  margin: 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: var(--mp-featureDescColor, #434655);
  opacity: 0.9;
`;

/* ─── Grid / Units Section ─── */
export const Section = styled.section`
  max-width: 1680px;
  margin: 0 auto;
  padding: 0 40px 80px;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 0 16px 48px;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 36px;
  flex-wrap: wrap;
  gap: 20px;
`;

export const CenteredSectionHeader = styled(SectionHeader)`
  justify-content: center;
  text-align: center;
`;

export const SectionTitleBlock = styled.div`
  flex: 1;
  min-width: 280px;
`;

export const SectionTitleBlockCentered = styled(SectionTitleBlock)`
  max-width: 620px;
`;

export const SectionSubtitle = styled.span`
  display: block;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: var(--mp-featureIconColor, #D863BA);

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    width: max-content;
  }
`;

export const SectionTitle = styled.h2`
  margin: 8px 0 0 0;
  font-family: 'Liberation Serif', Georgia, serif;
  font-size: 40px;
  line-height: 48px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 36px;
  }
`;

export const SectionDesc = styled.p`
  margin: 12px 0 0 0;
  font-family: 'Liberation Serif', Georgia, serif;
  font-size: 16px;
  line-height: 24px;
  color: var(--mp-featureDescColor, #434655);
  opacity: 0.85;
`;

export const TabBar = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const TabBtn = styled.button<{ $active?: boolean }>`
  padding: 8px 18px;
  border-radius: 9999px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${p => p.$active ? "var(--mp-featureIconColor, #D863BA)" : "rgba(23, 23, 23, 0.12)"};
  background: ${p => p.$active ? "var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%))" : "transparent"};
  color: ${p => p.$active ? "#ffffff" : "var(--mp-headingColor, #171717)"};
  transition: all 0.2s;

  &:hover {
    border-color: var(--mp-featureIconColor, #D863BA);
    background: ${p => p.$active ? "var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%))" : "rgba(216, 99, 186, 0.08)"};
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: ${p => p.$active ? "transparent" : "rgba(255, 255, 255, 0.1)"};
    background: ${p => p.$active ? "linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%)" : "transparent"};
    color: ${p => p.$active ? "#402D00" : "var(--mp-headingColor, #ffffff)"};

    &:hover {
      border-color: ${p => p.$active ? "transparent" : "rgba(255, 201, 81, 0.35)"};
      background: ${p => p.$active ? "linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%)" : "rgba(255, 255, 255, 0.05)"};
    }
  }
`;

export const UnitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const UnitCard = styled.div`
  background: var(--mp-cardBackground, #ffffff);
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.03);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.08);
    border-color: rgba(216, 99, 186, 0.2);
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #15181b;
    border-color: rgba(255, 255, 255, 0.06);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.25);

    &:hover {
      border-color: rgba(255, 201, 81, 0.2);
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.45);
    }
  }
`;

export const UnitCardHeader = styled.div`
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: rgba(23, 23, 23, 0.04);

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #1e2226;
  }
`;

export const UnitImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;

  ${UnitCard}:hover & {
    transform: scale(1.04);
  }
`;

export const UnitCardIconWrap = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--mp-cardBackground, #ffffff);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  color: var(--mp-featureIconColor, #D863BA);
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  font-size: 16px;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #15181b;
    color: #FFC951;
  }
`;

export const UnitBadge = styled.span`
  position: absolute;
  top: 16px;
  right: 16px;
  padding: 6px 12px;
  border-radius: 20px;
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #ffffff;
  background: var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%));

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    color: #402D00;
  }
`;

export const UnitCardBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const UnitCardTitle = styled.h3`
  margin: 0 0 8px 0;
  font-family: 'Liberation Serif', serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: var(--mp-headingColor, #171717);
`;

export const UnitCardDesc = styled.p`
  margin: 0 0 18px 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  line-height: 20px;
  color: var(--mp-featureDescColor, #434655);
  opacity: 0.9;
`;

export const UnitMetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
  padding-bottom: 18px;
  border-bottom: 1px solid rgba(23, 23, 23, 0.08);

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255, 255, 255, 0.08);
  }
`;

export const UnitMetaItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: var(--mp-featureDescColor, #434655);

  svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--mp-featureIconColor, #D863BA);
  }

  [data-marketplace-theme="dark"] & svg,
  .dark & svg {
    color: #FFC951;
  }
`;

export const UnitFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: auto;
`;

export const UnitPrice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  .label {
    font-family: Montserrat, sans-serif;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--mp-featureDescColor, #434655);
    opacity: 0.65;
  }

  .amount {
    font-family: 'Liberation Serif', serif;
    font-size: 20px;
    font-weight: 700;
    color: var(--mp-headingColor, #171717);
  }
`;

export const UnitPriceSuffix = styled.small`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  opacity: 0.75;
`;

export const BookUnitBtn = styled.button`
  padding: 10px 18px;
  border-radius: 10px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  border: none;
  background: var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%));
  color: var(--mp-heroBtnColor, #ffffff);
  cursor: pointer;
  transition: opacity 0.2s;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    color: #402D00;
  }
`;

/* ─── Highlights Section ─── */
export const HighlightSection = styled.section`
  background: var(--mp-cardBackground, #ffffff);
  padding: 80px 20px;
  transition: background-color 0.3s;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #0f1114;
  }

  @media (max-width: 480px) {
    padding: 48px 16px;
  }
`;

export const AgenciesSection = styled.section`
  background: #f5f6fa;
  padding: 80px 20px;
  width: 100%;
  box-sizing: border-box;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #1A1C1E;
  }

  @media (max-width: 480px) {
    padding: 48px 16px;
  }
`;

export const HighlightContainer = styled.div`
  max-width: 1680px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 0.95fr 1.05fr;
  gap: 64px;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

export const CollageArea = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  grid-template-rows: auto auto;
  gap: 16px;
`;

export const CollageImgWrap = styled.div<{ $spanRow?: number; $spanCol?: number }>`
  grid-row: span ${p => p.$spanRow || 1};
  grid-column: span ${p => p.$spanCol || 1};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(23, 23, 23, 0.08);
  aspect-ratio: 1;

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255, 255, 255, 0.06);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
  }
`;

export const StatFloatingCard = styled.div`
  position: absolute;
  top: 35%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #ffffff;
  color: #171717;
  padding: 32px 40px;
  border-radius: 22px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.08);
  text-align: center;
  z-index: 5;
  min-width: 170px;
  border: 1px solid rgba(23, 23, 23, 0.08);

  .num {
    font-family: 'Liberation Serif', serif;
    font-size: 48px;
    font-weight: 800;
    line-height: 1;
    margin-bottom: 8px;
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .txt {
    font-family: Montserrat, sans-serif;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #434655;
    opacity: 1;
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #171717;
    color: #ffffff;
    border-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.35);

    .txt {
      color: #ffffff;
      opacity: 0.85;
    }
  }
`;

export const AuthorCard = styled.div`
  display: inline-block;
  max-width: 360px;
  border-radius: 20px;
  padding: 20px 24px;
  background: #ffffff;
  border: 1px solid rgba(23, 23, 23, 0.08);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.04);
  margin-top: 12px;
  margin-bottom: 24px;

  .author-name {
    font-family: Montserrat, sans-serif;
    font-weight: 700;
    font-size: 14px;
    color: #171717;
    margin-bottom: 4px;
  }

  .author-title {
    font-family: 'Liberation Serif', serif;
    font-size: 15px;
    line-height: 22px;
    color: #434655;
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #15181b;
    border-color: rgba(255, 255, 255, 0.08);

    .author-name {
      color: #ffffff;
    }

    .author-title {
      color: #D2C5AF;
    }
  }
`;

export const HighlightContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const HighlightLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--mp-featureIconColor, #D863BA);
  letter-spacing: 1.2px;
  margin-bottom: 12px;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
`;

export const HighlightTitle = styled.h2`
  margin: 0 0 20px 0;
  font-family: 'Liberation Serif', Georgia, serif;
  font-size: 40px;
  font-weight: 700;
  line-height: 48px;
  color: var(--mp-headingColor, #171717);

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 36px;
  }
`;

export const HighlightDesc = styled.p`
  margin: 0 0 28px 0;
  font-family: 'Liberation Serif', Georgia, serif;
  font-size: 16px;
  line-height: 26px;
  color: var(--mp-featureDescColor, #434655);
  opacity: 0.9;
`;

export const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 32px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const BulletItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;

  span {
    color: #171717;
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    color: #ffffff;

    span {
      color: #ffffff;
    }
  }

  svg {
    color: #10b981;
    flex-shrink: 0;
  }
`;

export const CaseStudyBtn = styled.button`
  background: none;
  border: none;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  color: #171717;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  position: relative;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  [data-marketplace-theme="dark"] &,
  .dark & {
    color: #ffffff;
  }

  .arrows {
    display: inline-block;
    font-size: 16px;
    font-weight: 700;
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .gradient-line {
    display: inline-block;
    width: 48px;
    height: 2px;
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    border-radius: 2px;
    transition: width 0.2s;
    flex-shrink: 0;
  }

  &:hover .gradient-line {
    width: 64px;
  }
`;

/* ─── Agencies Section ─── */
export const AgenciesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

export const AgencyCard = styled.div`
  background: var(--mp-cardBackground, #ffffff);
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.03);
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.08);
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #15181b;
    border-color: rgba(255, 255, 255, 0.06);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  }
`;

export const AgencyHeader = styled.div`
  height: 120px;
  background: rgba(23, 23, 23, 0.04);
  position: relative;

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: #1e2226;
  }
`;

export const AgencyBgImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const AgencyLogo = styled.div`
  position: absolute;
  bottom: -28px;
  left: 50%;
  transform: translateX(-50%);
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
  border: 3px solid var(--mp-cardBackground, #ffffff);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Montserrat, sans-serif;
  font-weight: 700;
  color: #ffffff;
  font-size: 20px;

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: #1A1C1E; /* Matches updated dark background of the section */
  }
`;

export const AgencyBody = styled.div`
  padding: 40px 20px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;

export const AgencyName = styled.h3`
  margin: 0 0 6px 0;
  font-family: Liberation Serif;
  font-weight: 700;
  font-style: bold;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: 0px;
  text-align: center;
  vertical-align: middle;
  color: var(--mp-headingColor, #171717);
`;

export const AgencyMeta = styled.p`
  margin: 0 0 20px 0;
  font-family: Liberation Serif;
  font-weight: 400;
  font-style: regular;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
  text-align: center;
  vertical-align: middle;
  color: var(--mp-featureDescColor, #434655);
  opacity: 1;
`;

export const AgencyLink = styled.button`
  margin-top: auto;
  background: transparent;
  border: none;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: var(--mp-featureIconColor, #D863BA);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: gap 0.2s, opacity 0.2s;

  &:hover {
    gap: 8px;
    opacity: 0.85;
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }
`;

/* ─── Custom Form Section ─── */
export const FormSection = styled.section`
  max-width: 900px;
  margin: 0 auto;
  padding: 60px 20px 100px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 40px 16px 60px;
  }
`;

export const FormCard = styled.div`
  background: var(--mp-cardBackground, #ffffff);
  border-radius: 24px;
  padding: 48px;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.04);

  [data-marketplace-theme="dark"] &,
  .dark & {
    border-color: rgba(255, 255, 255, 0.06);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
  }

  @media (max-width: 576px) {
    padding: 28px 20px;
  }
`;

export const FormTitleBlock = styled.div`
  text-align: center;
  margin-bottom: 36px;
`;

export const FormTitle = styled.h2`
  margin: 0 0 10px 0;
  font-family: 'Liberation Serif', Georgia, serif;
  font-size: 36px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);

  span.gradient {
    font-style: italic;
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  [data-marketplace-theme="light"] & span.gradient,
  :root:not([data-marketplace-theme="dark"]) & span.gradient {
    background: var(--mp-heroAccent, linear-gradient(95.57deg, #FFD573 0%, #D863BA 100%));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

export const FormSubtitle = styled.p`
  margin: 0;
  font-family: 'Liberation Serif', Georgia, serif;
  font-size: 15px;
  line-height: 24px;
  color: var(--mp-featureDescColor, #434655);
  opacity: 0.85;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const FormGroup = styled.div<{ $span?: number; $mb?: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  grid-column: ${p => p.$span ? `span ${p.$span}` : "auto"};
  margin-bottom: ${p => p.$mb !== undefined ? `${p.$mb}px` : "0"};
`;

export const FormLabel = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--mp-headingColor, #171717);
`;

export const formFieldStyles = `
  padding: 12px 16px;
  border-radius: 10px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  background: var(--mp-bodyBackground, #f7f8fc);
  color: var(--mp-headingColor, #171717);
  border: 1px solid rgba(23, 23, 23, 0.12);
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    border-color: var(--mp-featureIconColor, #D863BA);
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: transparent;
    border: none;
    border-radius: 0;
    padding-left: 0;
    padding-right: 0;
    border-bottom: 1.5px solid rgba(255, 255, 255, 0.2);
    color: var(--mp-headingColor, #ffffff);

    &:focus {
      border-color: #FFC951;
    }
  }
`;

export const FormSelect = styled.select`
  ${formFieldStyles}
  cursor: pointer;
`;

export const FormInput = styled.input`
  ${formFieldStyles}
`;

export const FormTextarea = styled.textarea`
  ${formFieldStyles}
  min-height: 110px;
  resize: vertical;
`;

export const SubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 9999px;
  font-family: Montserrat, sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  border: none;
  background: var(--mp-heroBtnBg, linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%));
  color: var(--mp-heroBtnColor, #ffffff);
  transition: opacity 0.2s, transform 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    opacity: 0.95;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  [data-marketplace-theme="dark"] &,
  .dark & {
    background: linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%);
    color: #402D00;
  }
`;

export const FormNote = styled.p`
  margin: 16px 0 0 0;
  text-align: center;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: var(--mp-featureDescColor, #434655);
  opacity: 0.7;
`;
